import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Color } from '@sorare/core/src/atoms/buttons/Button';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { useTokenOfferBelongsToUser } from '@sorare/core/src/hooks/useTokenOfferBelongsToUser';

import { useCancelOffer } from '#src/hooks/offers/useCancelOffer.ts';

import type { CancelOffer_anyCard } from './__generated__/index.graphql.ts';

type Props = {
  card: CancelOffer_anyCard;
  variant: 'small' | 'medium';
  color?: Color;
  fullWidth?: boolean;
};

const Root = styled(Button)`
  flex-shrink: 0;
`;

export const CancelOffer = ({
  card: { myMintedSingleSaleOffer: offer },
  variant,
  color = 'destructive',
  fullWidth = false,
}: Props) => {
  const { showNotification } = useSnackNotificationContext();
  const [processing, setProcessing] = useState(false);
  const belongsToUser = useTokenOfferBelongsToUser();
  const cancelOffer = useCancelOffer();

  const { blockchainId } = offer!;

  const handleClick = async () => {
    setProcessing(true);
    const error = await cancelOffer(blockchainId!);
    if (!error) showNotification('cancelOffer');
    setProcessing(false);
  };

  if (offer && belongsToUser(offer)) {
    return (
      <Root
        loading={processing}
        color={color}
        size={variant}
        fullWidth={fullWidth}
        onClick={() => {
          handleClick();
        }}
      >
        <FormattedMessage defaultMessage="Cancel listing" />
      </Root>
    );
  }

  return null;
};

CancelOffer.fragments = {
  anyCard: gql`
    fragment CancelOffer_anyCard on AnyCardInterface {
      slug
      myMintedSingleSaleOffer @skip(if: $onlyPrimary) {
        id
        blockchainId
        ...useTokenOfferBelongsToUser_offer
      }
    }
    ${useTokenOfferBelongsToUser.fragments.offer}
  ` as TypedDocumentNode<CancelOffer_anyCard>,
};
